import { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Dialog from "@mui/material/Dialog";
import {
  useDeleteScheduleMutation,
  useGetScheduleQuery,
  useSuiteSchedulerQuery,
  useDeleteSchedular,
} from "../../useQuery";
import { Days, SchedulerOptions } from "../../components-new/utils/constants";
import { PillRadio } from "../../components-new/common";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  Button,
  FormControl,
  MenuItem,
  Select,
  styled,
  TextField,
  Typography,
  Paper,
  Stack,
  InputAdornment,
  IconButton,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import dayjs from "dayjs";
import { useTheme } from "@mui/material/styles";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";

import { useNotification } from "../../hooks";

const StyledLabel = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontSize: "13px",
  fontWeight: 500,
}));

const StyledButton = styled(Button)(({ theme }) => ({
  padding: "15px 28px",
  borderRadius: "8px",
  textTransform: "capitalize",
  textAlign: "center",
  fontSize: "17px",
  fontWeight: 600,
  lineHeight: "20px",
}));

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

const getSubfreqArray = (subfreq) => {
  if (typeof subfreq === "string") {
    const trimmed = subfreq.split(",").map((day) => day.trim());
    return trimmed.length === Days.length ? [...Days] : trimmed;
  }
  return [];
};

function getStyles(day, schedule, theme) {
  return {
    fontWeight: schedule?.includes(day)
      ? theme.typography.fontWeightMedium
      : theme.typography.fontWeightRegular,
  };
}

const ScheduleDisplay = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  marginBottom: theme.spacing(3),
  backgroundColor: theme.palette.background.default,
  border: `1px solid ${theme.palette.divider}`,
  width: "450px",
}));

const ScheduleItem = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
  marginBottom: theme.spacing(1),
}));

const ScheduleLabel = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  minWidth: "120px",
  fontWeight: 500,
}));

const ScheduleValue = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.primary,
  fontWeight: 500,
}));

const Scheduler = ({ suiteId }) => {
  const theme = useTheme();
  const notify = useNotification();

  const { data: scheduleData, refetchWithParams: getSchedule } = useGetScheduleQuery();
  const { refetchWithParams: updateSchedule } = useSuiteSchedulerQuery();
  const { mutate: deleteSchedule } = useDeleteScheduleMutation({
    onSuccess: () => {
      notify("Scheduler is Successfully Deleted", "success");
      getSchedule(suiteId); // Refresh current schedule after deletion
    },
    onError: (error) => {
      notify(error.message || "Failed to delete schedule", "error");
    },
  });

  const [currentSchedule, setCurrentSchedule] = useState(null); // For displaying current schedule
  const [formSchedule, setFormSchedule] = useState(null); // For form changes
  useEffect(() => {
    getSchedule(suiteId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [suiteId]);

  useEffect(() => {
    if (scheduleData) {
      setCurrentSchedule(scheduleData);
      setFormSchedule(scheduleData); // Initialize form with current schedule
    }
  }, [scheduleData]);

  const handleSchedulerChange = (event) => {
    const newFrequency = event.target.value;
    setFormSchedule((prevSchedule) => ({
      ...prevSchedule,
      frequency: newFrequency,
    }));
  };

  const handleTimeChange = (newTime) => {
    if (!newTime || !dayjs(newTime).isValid()) {
      setFormSchedule((prev) => ({
        ...prev,
        scheduletime: null,
      }));
      return;
    }

    const formattedTime = dayjs(newTime).format("HH:mm:ss") + ".0";

    setFormSchedule((prev) => ({
      ...prev,
      scheduletime: formattedTime,
    }));
  };


  const handleDayChange = (event) => {
    const {
      target: { value },
    } = event;

    setFormSchedule((prevSchedule) => ({
      ...prevSchedule,
      scheduletime: `${prevSchedule.scheduletime}`,
      subfreq: value.map((d) => d.trim()).join(","),
    }));
  };

  const handleDateChange = (newDate) => {
    setFormSchedule((prevSchedule) => {
      return {
        ...prevSchedule,
        // scheduletime: `${prevSchedule.scheduletime}.0`,
        scheduledate: dayjs(newDate).format("YYYY-MM-DD"),
      };
    });
  };

  const handleIntervalChange = (event) => {
    const value = event.target.value;
    // Only allow numbers between 1 and 60
    if (value === "" || (Number(value) >= 1 && Number(value) <= 1440)) {
      setFormSchedule((prevSchedule) => ({
        ...prevSchedule,
        repeatInterval: value === "" ? "" : Number(value),
      }));
    }
  };

  const validateSchedule = () => {
    if (!formSchedule?.frequency) {
      notify("Frequency is required", "error");
      return false;
    }

    if (!formSchedule?.scheduletime) {
      notify("Time is required", "error");
      return false;
    }

    // Strict HH:mm:ss validation
    const isValidTime = dayjs(
      formSchedule.scheduletime,
      "HH:mm:ss.0",
      true   // STRICT MODE
    ).isValid();

    if (!isValidTime) {
      notify("Please enter a valid time", "error");
      return false;
    }

    const today = dayjs().startOf("day");

    // Weekly validation
    if (formSchedule.frequency === "weekly") {
      const days = getSubfreqArray(formSchedule?.subfreq);
      if (!days || days.length === 0) {
        notify("Please select at least one day for weekly schedule", "error");
        return false;
      }
    }

    // One-time validation
    if (formSchedule.frequency === "other") {
      if (!formSchedule?.scheduledate) {
        notify("Please select a date", "error");
        return false;
      }

      if (dayjs(formSchedule.scheduledate).isBefore(today)) {
        notify("Scheduled date cannot be in the past", "error");
        return false;
      }
    }

    // Interval validation
    if (formSchedule?.repeatInterval !== undefined && formSchedule?.repeatInterval !== "") {
      const value  = formSchedule.repeatInterval;

      // Must be digits only (no decimals)
      if (!/^\d+$/.test(value )) {
        notify("Repeat interval must be a whole number (no decimals)", "error");
        return false;
      }

      const interval = Number(value);

      if (isNaN(interval) || interval < 1 || interval > 1440) {
        notify("Repeat interval must be between 1 and 1440 minutes", "error");
        return false;
      }
    }

    // Duplicate prevention
    const existing = scheduleData?.scheduler || [];

    const isDuplicate = existing.some((item) => {
      return (
        item.frequency === formSchedule.frequency &&
        item.scheduletime === formSchedule.scheduletime &&
        (item.subfreq || "") === (formSchedule.subfreq || "") &&
        (item.scheduledate || "") === (formSchedule.scheduledate || "")
      );
    });

    if (isDuplicate) {
      notify("A schedule with the same configuration already exists", "error");
      return false;
    }

    return true;
  };

  const handleSubmitSchedule = () => {
    if (!validateSchedule()) return;

    updateSchedule(formSchedule, suiteId).then((res) => {
      getSchedule(suiteId); // Refresh current schedule after successful update
      notify("Scheduler is Successfully Saved", "success");
    });
  };

  const handleDeleteSchedule = () => {
    deleteSchedule(suiteId);
  };

  const getFrequencyLabel = (frequency) => {
    return SchedulerOptions.find((option) => option.value === frequency)?.label || frequency;
  };

  const formatTime = (time) => {
    if (!time) return "Not set";
    return dayjs(time, "HH:mm:ss").format("hh:mm A");
  };

  const { mutate: setDeleteSchedular } = useDeleteSchedular({
    onSuccess: () => {
      notify("Deleted successfully", "success");
      getSchedule(suiteId); // Refresh schedule after deletion
    },
    onError: (error) => {
      notify(`Error adding Defect: ${error.message}`, "error");
    },
  });
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [scheduleToDelete, setScheduleToDelete] = useState(null);

  const handleDelete = (index) => {
    const scheduler = scheduleData?.scheduler[index];
    if (!scheduler) return;

    setScheduleToDelete(scheduler);
    setConfirmOpen(true);
  };

  const confirmDelete = () => {
      if (!scheduleToDelete) return;

      setDeleteSchedular([
        scheduleToDelete.idscheduler,
        scheduleToDelete.suiteid,
      ]);

      setConfirmOpen(false);
      setScheduleToDelete(null);
    };



  const CustomScheduleItem = ({ frequency = "", time = "", interval =0, subfreq= "", onDelete }) => {
    return (
      <Box
        sx={{
          border: "1px solid #ccc",
          borderRadius: 1,
          padding: 1,
          backgroundColor: "#f9f9f9",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          transition: "background-color 0.3s",
          "&:hover": {
            backgroundColor: "#ffffff",
          },
        }}
      >
        <Box>
          <Typography variant="body1">Frequency : {frequency}   --  On Days : {subfreq}</Typography>
          <Typography variant="body2" color="text.secondary">
            Time : {time} -- Repeat Every : {interval} mins
          </Typography>
        </Box>
        <IconButton
          onClick={onDelete}
          sx={{
            color: "#f44336",
          }}
        >
          <DeleteIcon />
        </IconButton>
      </Box>
    );
  };

  return (
    <>
    <Box display="flex" flexDirection="row" gap={2} width="100%">
      <Box>
        {/* {currentSchedule && (
          <ScheduleDisplay elevation={0}>
            <Typography variant="h6" gutterBottom>
              Current Schedule
            </Typography>
            <Stack spacing={1}>
              <ScheduleItem>
                <ScheduleLabel>Frequency:</ScheduleLabel>
                <ScheduleValue>{getFrequencyLabel(currentSchedule.frequency)}</ScheduleValue>
              </ScheduleItem>
              <ScheduleItem>
                <ScheduleLabel>Time:</ScheduleLabel>
                <ScheduleValue>{formatTime(currentSchedule.scheduletime)}</ScheduleValue>
              </ScheduleItem>
              {currentSchedule.repeatInterval && (
                <ScheduleItem>
                  <ScheduleLabel>Repeat Every:</ScheduleLabel>
                  <ScheduleValue>{currentSchedule.repeatInterval} minutes</ScheduleValue>
                </ScheduleItem>
              )}
              {currentSchedule.frequency === SchedulerOptions[1].value && (
                <ScheduleItem>
                  <ScheduleLabel>Days:</ScheduleLabel>
                  <ScheduleValue>
                    {getSubfreqArray(currentSchedule.subfreq).join(", ")}
                  </ScheduleValue>
                </ScheduleItem>
              )}
              {currentSchedule.frequency === SchedulerOptions[2].value && (
                <ScheduleItem>
                  <ScheduleLabel>Date:</ScheduleLabel>
                  <ScheduleValue>
                    {currentSchedule.scheduledate
                      ? dayjs(currentSchedule.scheduledate).format("MMMM D, YYYY")
                      : "Not set"}
                  </ScheduleValue>
                </ScheduleItem>
              )}
            </Stack>
          </ScheduleDisplay>
        )} */}

        <Box>
          <StyledLabel>Select Frequency</StyledLabel>
          {SchedulerOptions.map((option) => (
            <PillRadio
              key={option?.value}
              value={option?.value}
              label={option?.label}
              checked={formSchedule?.frequency === option.value}
              onChange={handleSchedulerChange}
            />
          ))}
        </Box>
        <Box my={2} display={"flex"} alignItems={"center"} columnGap={2} flexWrap="wrap" rowGap={2}>
          {formSchedule?.frequency === SchedulerOptions[2].value && (
            <Box>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <StyledLabel>Select Date</StyledLabel>
                <DatePicker
                  value={formSchedule.scheduledate ? dayjs(formSchedule.scheduledate) : null}
                  onChange={handleDateChange}
                  renderInput={(params) => <TextField {...params} />}
                />
              </LocalizationProvider>
            </Box>
          )}
          <Box>
            <StyledLabel>Repeat Every</StyledLabel>
            <TextField
              type="number"
              value={formSchedule?.repeatInterval || ""}
              onChange={handleIntervalChange}
              placeholder="Enter minutes"
              sx={{ width: "170px" }}
              InputProps={{
                endAdornment: <InputAdornment position="end">mins</InputAdornment>,
                inputProps: { min: 1, max: 1440 },
              }}
            />
          </Box>
          {formSchedule?.frequency === SchedulerOptions[1].value && (
            <Box>
              <FormControl sx={{ width: 257 }}>
                <StyledLabel>Select Day</StyledLabel>
                <Select
                  multiple
                  value={getSubfreqArray(formSchedule?.subfreq)}
                  onChange={handleDayChange}
                  MenuProps={MenuProps}
                >
                  {Days.map((day) => (
                    <MenuItem
                      key={day}
                      value={day}
                      style={getStyles(day, getSubfreqArray(formSchedule?.subfreq), theme)}
                    >
                      {day}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          )}
          <Box>
            <StyledLabel>Time</StyledLabel>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <TimePicker
                value={
                  formSchedule?.scheduletime ? dayjs(formSchedule?.scheduletime, "HH:mm:ss") : null
                }
                onChange={handleTimeChange}
                renderInput={(params) => <TextField {...params} />}
              />
            </LocalizationProvider>
          </Box>

        </Box>
        <Box mb={2}>
          <Box display="flex" mt={2} columnGap={2}>
            {/* <StyledButton
              sx={{ height: "50px" }}
              variant="contained"
              onClick={handleDeleteSchedule}
            >
              Delete
            </StyledButton> */}
            <StyledButton
              disabled={
                !formSchedule?.frequency ||
                !formSchedule?.scheduletime
              }
              sx={{ height: "50px" }}
              variant="contained"
              onClick={handleSubmitSchedule}
            >
              Submit
            </StyledButton>
          </Box>
        </Box>
      </Box>
      <Box sx={{ border: "1px solid #0000001f", p: 1, borderRadius: "inherit", width: "100%" }}>
        <Typography variant="h6" gutterBottom>
          Scheduled List
        </Typography>
        <Box
          sx={{
            maxHeight: 300,
            overflowY: "auto",
            mt: 1,
            display: "flex",
            flexDirection: "column",
            gap: 1,
          }}
        >
          {scheduleData?.scheduler?.map((item, index) => (
            <CustomScheduleItem
              key={index}
              frequency={item.frequency}
              time={item.scheduletime}
              interval={item.interval}
              subfreq={item.subfreq}
              onDelete={() => handleDelete(index)}
            />
          ))}
        </Box>
      </Box>
    </Box>

    <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
    <Box p={3} minWidth={300}>
      <Typography variant="h6" gutterBottom>
        Delete Schedule?
      </Typography>

      <Typography variant="body2" color="text.secondary" mb={2}>
        This action cannot be undone.
      </Typography>

      <Box display="flex" justifyContent="flex-end" gap={2}>
        <Button onClick={() => setConfirmOpen(false)}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={confirmDelete}
        >
          Delete
        </Button>
      </Box>
    </Box>
  </Dialog>
    </>
  );
};

export default Scheduler;
